import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Gauge, ChevronDown } from 'lucide-react';
import {
  MagnifyingGlassIcon,
  PlusCircleIcon,
  MinusCircleIcon,
  TrashIcon,
  ClockIcon,
  SignalIcon,
  UserIcon,
  UserMinusIcon,
  UsersIcon,
} from '@heroicons/react/24/solid';
import './theme.css';

const API_BASE = '/api';

const statusConfig = {
  registered: {
    label: 'Registered',
    icon: UserIcon,
    color: 'text-[var(--status-success)]',
    badge: 'border border-[var(--status-success)] text-[var(--status-success)]',
  },
  pending: {
    label: 'Pending',
    icon: ClockIcon,
    color: 'text-[var(--status-warn)]',
    badge: 'border border-[var(--status-warn)] text-[var(--status-warn)]',
  },
  disabled: {
    label: 'Disabled',
    icon: UserMinusIcon,
    color: 'text-[var(--text-muted)]',
    badge: 'border border-[var(--text-muted)] text-[var(--text-muted)]',
  },
};

const StatusBadge = ({ status }) => {
  const { icon: Icon, badge, color, label } = statusConfig[status] || {};
  return (
    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm ${badge}`}>
      <Icon className={`w-4 h-4 ${color}`} />
      {label || 'Unknown'}
    </span>
  );
};

export default function UsersPage({ token = 'demo-token', role = 'admin' }) {
  const [users, setUsers] = useState([]);
  const [formEmail, setFormEmail] = useState('');
  const [formRole, setFormRole] = useState('Viewer');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [apiMessage, setApiMessage] = useState('');
  const [apiSuccess, setApiSuccess] = useState(null);
  const [showInviteForm, setShowInviteForm] = useState(false);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API_BASE}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(
        res.data.map((u) => ({
          id: u.id,
          email: u.email,
          role: u.role,
          status: u.status || 'ERROR',
          invitedAt: new Date(u.created_at).toISOString().slice(0, 10),
        }))
      );
    } catch (err) {
      console.error('Failed to load users', err);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const statusCounts = users.reduce((acc, d) => {
    acc[d.status] = (acc[d.status] || 0) + 1;
    return acc;
  }, {});

  const handleInvite = async () => {
    if (!formEmail.trim()) {
      setApiMessage('Email is required');
      setApiSuccess(false);
      return;
    }

    try {
      const res = await axios.post(
        `${API_BASE}/admin/user`,
        {
          email: formEmail,
          role: formRole.toLowerCase(),
          org_id: 'current-org-id',
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = res.data;
      setUsers([
        {
          id: data.id,
          email: data.email,
          role: data.role,
          status: 'pending',
          invitedAt: new Date().toISOString().slice(0, 10),
        },
        ...users,
      ]);
      setFormEmail('');
      setFormRole('Viewer');
      setApiMessage('Invitation sent.');
      setApiSuccess(true);
    } catch (err) {
      console.error('Invite error:', err);
      setApiMessage(err.response?.data?.detail || 'Error inviting user');
      setApiSuccess(false);
    }
  };

  const handleRemove = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await axios.delete(`${API_BASE}/admin/user/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchUsers();
    } catch (err) {
      console.error('Delete failed', err);
    }
  };

  const filteredUsers = users
    .filter((u) => u.email.toLowerCase().includes(searchQuery.toLowerCase()))
    .filter((u) => !statusFilter || u.status === statusFilter);

  return (
    <div className="flex flex-col h-full space-y-10 text-[var(--text-primary)] font-[var(--font-ui)]">
      <div className="space-y-1 border-b border-[var(--surface-800)] pb-4">
        <h1 className="text-2xl font-bold tracking-wide">User Management</h1>
        <p className="text-sm text-[var(--text-muted)]">Manage users, roles, and invitations</p>
      </div>

      {/* Top Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        <div className="p-4 rounded-xl border bg-[var(--surface-900)] border-[var(--surface-800)]">
          <div className="flex items-center gap-3">
            <UsersIcon className="w-6 h-6 text-[var(--text-muted)]" />
            <span className="text-lg font-semibold text-[var(--text-muted)]">Total Users</span>
          </div>
          <p className="mt-2 text-3xl font-bold text-[var(--text-primary)]">{users.length}</p>
        </div>

        {Object.entries(statusConfig).map(([key, cfg]) => (
          <div
            key={key}
            onClick={() => setStatusFilter(statusFilter === key ? '' : key)}
            className={`cursor-pointer p-4 rounded-xl border transition 
              ${statusFilter === key ? 'bg-[var(--surface-800)] border-[var(--signal-cyan)]' : 'bg-[var(--surface-900)] border-[var(--surface-800)]'}`}
          >
            <div className="flex items-center gap-3">
              <cfg.icon className={`w-6 h-6 ${cfg.color}`} />
              <span className={`text-lg font-semibold ${cfg.color}`}>{cfg.label}</span>
            </div>
            <p className="mt-2 text-3xl font-bold">{statusCounts[key] || 0}</p>
          </div>
        ))}
      </div>

      {/* Invite Form */}
      {role === 'admin' && (
        <div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowInviteForm(!showInviteForm)}
              className="flex items-center gap-2 text-[var(--signal-cyan)] border border-[var(--signal-cyan)] px-4 py-2 rounded-md hover:bg-[var(--surface-800)] transition"
            >
              {showInviteForm ? <MinusCircleIcon className="w-5 h-5" /> : <PlusCircleIcon className="w-5 h-5" />}
              {showInviteForm ? 'Hide Invite Form' : 'Invite User'}
            </button>
            {apiMessage && (
              <span
                className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all duration-300 ${
                  apiSuccess ? 'text-[var(--status-success)]' : 'text-[var(--status-error)]'
                }`}
              >
                {apiMessage}
              </span>
            )}
          </div>
          {showInviteForm && (
            <div className="mt-4 bg-[var(--surface-900)] border border-[var(--surface-800)] p-6 rounded-xl">
              <h2 className="text-xl font-semibold mb-4">Invite User</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <input
                  className="px-4 py-2 bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-md"
                  placeholder="User email"
                  value={formEmail}
                  onChange={(e) => setFormEmail(e.target.value)}
                />
                <div className="relative">
                  <select
                    className="w-full appearance-none pl-4 pr-9 py-2 bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-md text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--signal-cyan)] focus:border-[var(--signal-cyan)] transition-colors"
                    value={formRole}
                    onChange={(e) => setFormRole(e.target.value)}
                  >
                    <option value="Viewer">Viewer</option>
                    <option value="Admin">Admin</option>
                  </select>
                  <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                </div>
              </div>
              <button
                onClick={handleInvite}
                className="mt-4 text-[var(--status-success)] border border-[var(--status-success)] px-4 py-2 rounded-md hover:bg-[var(--surface-800)] transition"
              >
                Submit
              </button>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 relative">
          <MagnifyingGlassIcon className="absolute left-3 top-2.5 w-5 h-5 text-[var(--text-muted)]" />
          <input
            type="search"
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-md text-[var(--text-primary)]"
          />
        </div>
        <div className="relative">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full appearance-none pl-4 pr-9 py-2 bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-md text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--signal-cyan)] focus:border-[var(--signal-cyan)] transition-colors"
          >
            <option value="">All Statuses</option>
            <option value="registered">Registered</option>
            <option value="pending">Pending</option>
            <option value="disabled">Disabled</option>
          </select>
          <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
        </div>
      </div>

      {/* User Table */}
      <div className="overflow-x-auto bg-[var(--surface-900)] border border-[var(--surface-800)] rounded-xl">
        <table className="min-w-full text-sm text-[var(--text-primary)]">
          <thead className="bg-[var(--surface-800)] text-[var(--text-muted)]">
            <tr>
              <th className="px-4 py-2 text-left">Email</th>
              <th className="px-4 py-2 text-left">Role</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-left">Invited At</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center py-6 text-[var(--text-muted)]">
                  No matching users found.
                </td>
              </tr>
            ) : (
              filteredUsers.map((user) => (
                <tr key={user.id} className="border-b border-[var(--surface-800)] hover:bg-[var(--surface-800)]">
                  <td className="px-4 py-2 font-medium">{user.email}</td>
                  <td className="px-4 py-2">{user.role}</td>
                  <td className="px-4 py-2">
                    <StatusBadge status={user.status} />
                  </td>
                  <td className="px-4 py-2">{user.invitedAt}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => handleRemove(user.id)}
                      className="text-[var(--status-error)] hover:text-[var(--text-primary)]"
                      aria-label="Remove user"
                      title="Remove user"
                    >
                      <TrashIcon className="w-5 h-5 inline" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
